
    import { computed, defineComponent, ref } from 'vue'
    import { useRouter } from 'vue-router'

    import SearchInputForm from './search-input-form.vue'

    import { RadioButtonProps, RadioGroup } from '@progress/kendo-vue-inputs'

    import { buildLocalization, StringUtil, MaterialApi } from '@/util'
    import { useUserStore } from '@/store/user'
    import { useUserStateStore } from '@/store/userState'
    import { AxiosError, HttpStatusCode } from 'axios'

    export default defineComponent({
        name: 'SearchByPartNumber',

        components: {
            SearchInputForm,
            RadioGroup
        },

        setup() {
            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            const router = useRouter()
            const userStore = useUserStore()
            const userStateStore = useUserStateStore()
            const materialApi = new MaterialApi()
            const formRef = ref<InstanceType<typeof SearchInputForm> | null>(null)
            const searchMode = computed({
                get: () => userStateStore.sparePartSearchMode,
                set: (value) => userStateStore.setSparePartSearchMode(value)
            })

            /********************************************************************************************************************
             COMPUTED PROPERTIES
             ********************************************************************************************************************/
            const hasPartnerMaterials = computed(() => userStore.hasPartnerMaterials)

            const searchModeChoices = computed(() => [
                { label: localize('JoniMaterialNo'), value: 'joni' },
                { label: localize('OwnMaterialNo'), value: 'partner' }
            ] as RadioButtonProps[])

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function handleLookupError(err: AxiosError) {
                formRef.value?.setError(
                    err.response?.status == HttpStatusCode.NotFound
                        ? localize('UnknownPartNo')
                        : err.message
                )
            }

            function redirectToMaterial(materialNumber: number) {
                router.push({ name: 'material', params: { matno: materialNumber } })
            }

            /********************************************************************************************************************
             PUBLIC METHODS AND EVENT HANDLERS
             ********************************************************************************************************************/
            const { localize } = buildLocalization()

            function findSparePart(partNoText: string) {
                if (StringUtil.isNullOrEmpty(partNoText)) {
                    formRef.value?.setError(localize('UnknownPartNo'))
                    return
                }

                if (searchMode.value === 'partner') {
                    findByPartnerMaterialNo(partNoText)
                } else {
                    findByMaterialNo(partNoText)
                }
            }

            function findByMaterialNo(partNoText: string) {
                const materialNumber = parseInt(partNoText)

                if (isNaN(materialNumber)) {
                    formRef.value?.setError(localize('UnknownPartNo'))
                    return
                }

                materialApi.sparePartExists(materialNumber)
                    .then(response => {
                        if (response.data === true) {
                            redirectToMaterial(materialNumber)
                        } else {
                            formRef.value?.setError(localize('UnknownPartNo'))
                        }
                    })
                    .catch((err: AxiosError) => handleLookupError(err))
            }

            function findByPartnerMaterialNo(partnerMaterialNo: string) {
                materialApi.findSparePartByPartnerMaterialNo(partnerMaterialNo)
                    .then(response => {
                        redirectToMaterial(response.data)
                    })
                    .catch((err: AxiosError) => handleLookupError(err))
            }

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return {
                formRef,
                localize,
                searchMode,
                searchModeChoices,
                hasPartnerMaterials,
                findSparePart
            }
        }
    })
